import React, { useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Page, ResponsiveImage } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import CustomFooter from '../../components/CustomFooter/CustomFooter';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import Field from '../PageBuilder/Field/Field';
import { getMetadata } from '../PageBuilder/PageBuilder.helpers';
import css from './AboutPage.module.css';

// ============================================================
// HERO SECTION
// ============================================================

const HeroSection = ({ data }) => {
  if (!data) return null;

  const { title, description, blocks = [] } = data;

  // Blocks 0,1,2 are images; block 3 (Details) has the description text
  const imageBlocks = blocks.filter(b => b.media?.fieldType === 'image');
  const detailsBlock = blocks.find(b => b.blockName === 'Details');

  return (
    <section className={css.heroSection}>
      <div className={css.container}>
        {title?.content && <h1 className={css.heroTitle}>{title.content}</h1>}
        {description?.content && (
          <p className={css.heroSubtitle}>{description.content}</p>
        )}

        {imageBlocks.length > 0 && (
          <div className={css.heroImageGrid}>
            {imageBlocks.slice(0, 3).map((block, idx) => {
              const imageAsset = block.media?.image;
              const variantsArray = imageAsset?.attributes?.variants
                ? Object.keys(imageAsset.attributes.variants)
                : ['landscape-crop', 'landscape-crop-2x'];

              return (
                <div key={idx} className={css.heroImageCard}>
                  {imageAsset && (
                    <ResponsiveImage
                      image={imageAsset}
                      variants={variantsArray}
                      alt={block.media?.alt || `About image ${idx + 1}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================================
// MISSION SECTION
// ============================================================

const MissionSection = ({ data }) => {
  const blocks = data?.blocks || [];

  const missionTextObj = blocks[0]?.text;
  const ctaBlock = blocks[1];
  const partnersBlock = blocks[2];

  return (
    <section className={css.missionSection}>
      <div className={css.container}>
        {missionTextObj?.content && (
          <div className={css.missionText}>
            <Field data={missionTextObj} />
          </div>
        )}

        {ctaBlock && ctaBlock.callToAction?.href && (
          <div className={css.missionCta}>
            {ctaBlock.callToAction.fieldType === 'externalButtonLink' || ctaBlock.callToAction.href.startsWith('http') ? (
              <a
                href={ctaBlock.callToAction.href}
                target="_blank"
                rel="noopener noreferrer"
                className={css.missionCtaButton}
              >
                <Field data={ctaBlock.text} />
              </a>
            ) : (
              <Link
                to={ctaBlock.callToAction.href}
                className={css.missionCtaButton}
              >
                <Field data={ctaBlock.text} />
              </Link>
            )}
          </div>
        )}

        {partnersBlock && partnersBlock.text?.content && (
          <div className={css.missionPartners}>
            <div className={css.missionPartnersText}>
              <Field data={partnersBlock.text} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================================
// FEATURE SECTION — generic alternating layout
// ============================================================

const FeatureSection = ({ block, reversed, showQuote, showBadge, altBg }) => {
  if (!block) return null;

  const imageAsset = block.media?.image;
  const variantsArray = imageAsset?.attributes?.variants
    ? Object.keys(imageAsset.attributes.variants)
    : ['landscape-crop', 'landscape-crop-2x'];

  const checkItems = block.text?.content
    ? block.text.content
      .split('\n')
      .map(l => l.replace(/^[-*•]\s*/, '').trim())
      .filter(Boolean)
    : [];

  return (
    <section className={`${css.featureSection}${altBg ? ` ${css.altBg}` : ''}`}>
      <div className={css.container}>
        <div className={`${css.featureInner}${reversed ? ` ${css.reversed}` : ''}`}>
          {/* Image side */}
          <div className={css.featureImageWrapper}>
            {imageAsset && (
              <ResponsiveImage
                image={imageAsset}
                variants={variantsArray}
                alt={block.media?.alt || block.title?.content}
              />
            )}

            {showBadge && (
              <div className={css.featureBadge}>
                <div className={css.featureBadgeIcon}>
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
                  </svg>
                </div>
                <div>
                  <div className={css.featureBadgeStat}>
                    <FormattedMessage id="AboutPage.featureBadgeStat" />
                  </div>
                  <div className={css.featureBadgeLabel}>
                    <FormattedMessage id="AboutPage.featureBadgeLabel" />
                  </div>
                </div>
              </div>
            )}

            {showQuote && (
              <div className={css.featureQuote}>
                <p className={css.featureQuoteText}>"
                  <FormattedMessage id="AboutPage.featureQuote" />
                  "
                </p>
                <span className={css.featureQuoteAuthor}>
                  <FormattedMessage id="AboutPage.featureQuoteAuthor" />
                </span>
              </div>
            )}
          </div>

          {/* Text side */}
          <div className={css.featureContent}>
            <h2 className={css.featureTitle}>{block.title?.content}</h2>
            {checkItems.length > 1 ? (
              <>
                <p className={css.featureDescription}>{checkItems[0]}</p>
                <ul className={css.featureChecklist}>
                  {checkItems.slice(1).map((item, idx) => (
                    <li key={idx} className={css.featureCheckItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className={css.featureDescription}>{block.text?.content}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// TEAM SECTION
// ============================================================

const TeamSection = ({ data }) => {
  if (!data || !data.blocks?.length) return null;

  const { title, description, blocks } = data;

  return (
    <section className={css.teamSection}>
      <div className={css.container}>
        <div className={css.teamHeader}>
          {title?.content && <h2 className={css.teamTitle}>{title.content}</h2>}
          {description?.content && (
            <p className={css.teamSubtitle}>{description.content}</p>
          )}
        </div>

        <div className={css.teamGrid}>
          {blocks.map((member, idx) => {
            const imageAsset = member.media?.image;
            const variantsArray = imageAsset?.attributes?.variants
              ? Object.keys(imageAsset.attributes.variants)
              : ['landscape-crop', 'landscape-crop-2x'];

            return (
              <div key={idx} className={css.teamCard}>
                <div className={css.teamCardHeader}>
                  <p className={css.teamMemberName}>{member.title?.content}</p>
                  <p className={css.teamMemberRole}>{member.text?.content}</p>
                </div>
                <div className={css.teamImageWrapper}>
                  {imageAsset && (
                    <figure>
                      <ResponsiveImage
                        image={imageAsset}
                        variants={variantsArray}
                        alt={member.media?.alt || member.title?.content}
                        rootClassName={css.teamMemberImage}
                      />
                    </figure>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

/**
 * AboutPage — Custom about page rendering data from Sharetribe Console
 * Route: /p/about
 */
export const AboutPageComponent = props => {
  const { pageAssetsData } = props;
  const config = useConfiguration();
  const intl = useIntl();

  const aboutPageData = pageAssetsData?.aboutPage?.data || {};
  const sections = aboutPageData?.sections || [];

  // Map sections by sectionName
  const heroData = useMemo(
    () => sections.find(s => s?.sectionName?.toLowerCase() === 'aout_data#09887'),
    [sections]
  );
  const contentData = useMemo(
    () => sections.find(s => s?.sectionName?.toLowerCase() === 'about_content#89001'),
    [sections]
  );
  const detailsData = useMemo(
    () => sections.find(s => s?.sectionName?.toLowerCase() === 'about_detailscontent#95543'),
    [sections]
  );
  const teamData = useMemo(
    () => sections.find(s => s?.sectionName?.toLowerCase() === 'our_team#74990'),
    [sections]
  );

  const featureBlocks = contentData?.blocks || [];

  const meta = aboutPageData?.meta || {};
  const pageMeta = getMetadata(meta, 'AboutPage');

  const fallbackTitle = 'About Us | ReThrow';
  const fallbackDescription = 'Learn more about the ReThrow disc golf marketplace.';

  const title = pageMeta.title || fallbackTitle;
  const description = pageMeta.description || fallbackDescription;
  const schema = {
    ...pageMeta.schema,
    name: title,
    description: description,
  };

  return (
    <Page
      schema={schema}
      title={title}
      description={description}
      socialSharing={pageMeta.socialSharing}
    >
      <div>
        <TopbarContainer currentPage="AboutPage" />

        <main>
          {/* 1. HERO — 3-image grid with title */}
          <HeroSection data={heroData} />

          {/* 2. MISSION + STATS */}
          <MissionSection data={detailsData} />

          {/* 3. FEATURE BLOCK 1 — text left, image right with badge */}
          {featureBlocks[0] && (
            <FeatureSection block={featureBlocks[0]} showBadge altBg />
          )}

          {/* 4. FEATURE BLOCK 2 — image left with quote, text right */}
          {featureBlocks[1] && (
            <FeatureSection block={featureBlocks[1]} reversed showQuote />
          )}

          {/* 5. TEAM GRID */}
          <TeamSection data={teamData} />
        </main>

        <CustomFooter />
      </div>
    </Page>
  );
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

const AboutPage = compose(connect(mapStateToProps))(AboutPageComponent);

export default AboutPage;
